/*  eslint-disable react/no-unescaped-entities */

import React, { ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import { E2EFlow } from '../../../types/Feature';
import FlowChoices from './components/FlowChoices';
import { ShopTable } from './components/ShopTable';
import { useScheduledFeatureContext } from './ScheduledFeatureContext';

interface E2EFlowFormSectionProps {
    e2eFlowConfig: E2EFlow;
    setE2EFlowConfig: (e2eFlowConfig: E2EFlow) => void;
    isDetailMode: boolean;
}

const Container = styled.div`
    display: flex;
    justify-content: center;
`;

const Content = styled.div`
    width: 75%;
    display: flex;
    flex-direction: column;

    justify-content: center;
    > div {
        margin: 16px 0;
    }
`;

const Title = styled.h2`
    width: 100%;
    text-align: left;
    margin: 16px 0;
`;

const Description = styled.div`
    text-align: left;
    font-size: 15px;
`;

const BehaviorDescription = styled.div`
    text-align: center;
    font-size: 20px;
    margin: 8px;
`;

const ShopContainer = styled.div<{ isE2EFlow: boolean }>`
    filter: ${({ isE2EFlow }) => (isE2EFlow ? 'none' : 'opacity(0.1) blur(0.5px) ')};
`;

const E2EFlowSection: React.FC<E2EFlowFormSectionProps> = ({
    e2eFlowConfig,
    isDetailMode,
    setE2EFlowConfig,
}: E2EFlowFormSectionProps): React.ReactElement => {
    const { shops } = useScheduledFeatureContext();

    const e2eFlowBehaviorDescription: JSX.Element = useMemo(() => {
        if (shops.length === 0) {
            return (
                <>
                    No shops configured, <b>ALL</b> job creation from this client will be SCHEDULED
                </>
            );
        }

        const numberOfEnabledShops = shops.filter((shop) => shop.enabled).length;

        if (numberOfEnabledShops === 0) {
            return (
                <>
                    No shops are enabled, <b>ALL</b> job creation from this client will be SCHEDULED
                </>
            );
        }

        if (numberOfEnabledShops === 1) {
            return (
                <>
                    Only <b>1</b> shop is enabled, ONLY job creation from this shop will be SCHEDULED
                </>
            );
        }
        return (
            <>
                <b>{numberOfEnabledShops}</b> shops are enabled, ONLY job creation from these shops will be SCHEDULED
            </>
        );
    }, [shops]);

    return (
        <>
            <Container>
                <Content>
                    <Title>Order Flow</Title>
                    <Description>
                        Select the configuration for this Scheduled client and set up shops to be impacted by this
                    </Description>
                    <FlowChoices
                        isE2EFlow={e2eFlowConfig?.enabled}
                        setIsE2EFlow={(isE2EFlow: boolean): void => {
                            setE2EFlowConfig({ ...e2eFlowConfig, enabled: isE2EFlow });
                        }}
                        isDetailMode={isDetailMode}
                    />

                    <ShopContainer isE2EFlow={e2eFlowConfig?.enabled}>
                        <Title>Select shop to be impacted</Title>
                        <Description>
                            Upon receiving a job creation request, we consult the table below to determine if a
                            scheduled package should be created. We compare all the shop's pickup addresses or
                            assignment codes with the corresponding values of the job's pickup address or assignment
                            code. If we find a match between a shop and the job, and the shop is enabled, we create a
                            scheduled package. Otherwise, we create an instant job instead. <br />
                            Remember to save your changes when editing the table!
                            <br />
                            <br />
                            <br />
                            <i>
                                Note: if no shop is enabled or no shop is configure, ALL job creations from this client
                                will be scheduled.
                            </i>
                            <br />
                            <br />
                        </Description>
                        <BehaviorDescription>Current behavior: {e2eFlowBehaviorDescription}</BehaviorDescription>
                        <ShopTable isEditable={!isDetailMode || !e2eFlowConfig?.enabled} />
                    </ShopContainer>
                </Content>
            </Container>
        </>
    );
};

export default E2EFlowSection;
